/**
 * After PromoTix-hosted seller login, the app reloads on the reseller origin and
 * Topbar completes SSO via cookie. React Router `location.state` is lost across
 * that external navigation, so we persist the intended return path here.
 */
const STORAGE_KEY = 'promotixResellerPostSellerLoginReturn';
const MAX_AGE_MS = 25 * 60 * 1000;

function isResellerAuthRoutePathname(pathname) {
  if (typeof pathname !== 'string') {
    return false;
  }
  return (
    pathname === '/login' ||
    pathname === '/signup' ||
    pathname === '/event-discovery/login' ||
    pathname === '/event-discovery/signup'
  );
}

function isSafeInternalReturnPath(path) {
  if (typeof path !== 'string' || path.length === 0 || path.length > 4096) {
    return false;
  }
  if (!path.startsWith('/') || path.startsWith('//')) {
    return false;
  }
  if (/[\n\r\\]/.test(path)) {
    return false;
  }
  return true;
}

export function setPostSellerLoginReturnPath(pathWithQueryAndHash) {
  if (typeof window === 'undefined' || !isSafeInternalReturnPath(pathWithQueryAndHash)) {
    return;
  }
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ path: pathWithQueryAndHash, t: Date.now() })
    );
  } catch (e) {
    // ignore quota / private mode
  }
}

/**
 * Call from onClick on links to the PromoTix seller app (https).
 * Skips when already on /login or /signup so a value set by checkout or
 * NamedRedirect (state.from) is not overwritten with /login.
 */
export function persistCurrentLocationBeforeSellerAppNavigation(targetHref) {
  if (typeof window === 'undefined' || typeof targetHref !== 'string') {
    return;
  }
  if (!/^https?:\/\//i.test(targetHref)) {
    return;
  }
  const { pathname, search, hash } = window.location;
  if (isResellerAuthRoutePathname(pathname)) {
    return;
  }
  const path = `${pathname}${search}${hash}`;
  setPostSellerLoginReturnPath(path);
}

/**
 * @returns {string|null} pathname+search+hash for history.replace, or null
 */
export function clearPostSellerLoginReturnPath() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

const parseStoredReturnPath = raw => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    const path = parsed && typeof parsed.path === 'string' ? parsed.path : null;
    const t = typeof parsed.t === 'number' ? parsed.t : 0;
    if (!path || Date.now() - t > MAX_AGE_MS) {
      return null;
    }
    return isSafeInternalReturnPath(path) ? path : null;
  } catch (e) {
    return null;
  }
};

/** Read persisted return path without removing it (for redirect priority). */
export function peekPostSellerLoginReturnPath() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return parseStoredReturnPath(window.sessionStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return null;
  }
}

export function consumePostSellerLoginReturnPath() {
  if (typeof window === 'undefined') {
    return null;
  }
  let raw;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
  return parseStoredReturnPath(raw);
}
